/**
 * Peças de interface.
 *
 * Duas regras atravessam tudo aqui, e as duas vêm de como o barbeiro usa o app:
 * em pé, com uma mão, entre um corte e outro.
 *
 *  1. Alvo de toque nunca menor que 44px.
 *  2. Ação principal embaixo, no alcance do polegar — nunca no topo da tela.
 */

import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

function cx(...classes: Array<string | false | undefined | null>): string {
  return classes.filter(Boolean).join(' ');
}

// ── Botão ───────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  full?: boolean;
  loading?: boolean;
}

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-ink active:bg-primary-hover',
  secondary: 'bg-surface-raised text-ink border border-line active:bg-surface-sunken',
  ghost: 'text-ink-soft active:bg-surface-raised',
  danger: 'bg-surface-raised text-danger border border-line active:bg-surface-sunken',
};

export function Button({
  variant = 'primary',
  full,
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={cx(
        'touch-target inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3',
        'text-[15px] font-semibold transition-colors select-none',
        'disabled:opacity-50',
        BUTTON_STYLES[variant],
        full && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </button>
  );
}

// ── Campo ───────────────────────────────────────────────────────────────────

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

export function Field({ label, error, hint, className, id, ...rest }: FieldProps) {
  const fieldId = id ?? `campo-${label.toLowerCase().replace(/\W+/g, '-')}`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-[13px] font-medium text-ink-soft">
        {label}
      </label>
      <input
        id={fieldId}
        className={cx(
          'touch-target w-full rounded-xl border bg-surface-raised px-4 py-3',
          // 16px é o mínimo que o Safari do iPhone aceita sem dar zoom no foco.
          'text-[16px] text-ink placeholder:text-ink-faint',
          'outline-none focus:border-primary',
          error ? 'border-danger' : 'border-line',
          className,
        )}
        {...rest}
      />
      {error && <p className="text-[13px] text-danger">{error}</p>}
      {hint && !error && <p className="text-[13px] text-ink-faint">{hint}</p>}
    </div>
  );
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  hint?: string;
}

/** Mesmo campo, para o que não cabe numa linha — observação da ficha, recado. */
export function TextArea({ label, error, hint, className, id, rows = 3, ...rest }: TextAreaProps) {
  const fieldId = id ?? `campo-${label.toLowerCase().replace(/\W+/g, '-')}`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-[13px] font-medium text-ink-soft">
        {label}
      </label>
      <textarea
        id={fieldId}
        rows={rows}
        className={cx(
          'w-full rounded-xl border bg-surface-raised px-4 py-3',
          'text-[16px] text-ink placeholder:text-ink-faint',
          'outline-none focus:border-primary',
          error ? 'border-danger' : 'border-line',
          className,
        )}
        {...rest}
      />
      {error && <p className="text-[13px] text-danger">{error}</p>}
      {hint && !error && <p className="text-[13px] text-ink-faint">{hint}</p>}
    </div>
  );
}

// ── Folha inferior ──────────────────────────────────────────────────────────

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Barra fixa embaixo, no alcance do polegar. */
  footer?: ReactNode;
}

/**
 * Sobe de baixo, como todo modal do iOS. É a forma certa para o polegar: a
 * ação fica perto de onde a mão já está, e fechar é um gesto para baixo.
 *
 * Vai para o `body` por portal, e não para onde foi escrita. Toda folha nasce
 * dentro do `<main>` do App, que rola e por isso ganha camada própria no
 * WebKit: ali dentro, `z-50` só disputa com os irmãos da folha, e a barra de
 * navegação — que vem depois no documento — é pintada por cima. No iPhone o
 * efeito era o rodapé da folha desaparecer atrás das abas: o "Marcar" existia,
 * ocupava exatamente a faixa da barra de baixo e não dava para tocar. No
 * Chrome do computador nada disso aparece, que é o que fez o defeito durar.
 */
export function Sheet({ open, onClose, title, children, footer }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    // Trava a rolagem de trás, senão o iOS rola a página em vez da folha.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <button
        type="button"
        aria-label="Fechar"
        className="animate-fade absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="animate-sheet relative flex max-h-[92dvh] flex-col rounded-t-3xl border-t border-line bg-surface">
        <div className="flex shrink-0 items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-[17px] font-semibold text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="touch-target -mr-2 px-2 text-[15px] font-medium text-ink-soft"
          >
            Fechar
          </button>
        </div>

        <div className="scroll-touch flex-1 overflow-y-auto px-4 py-4">{children}</div>

        {footer && (
          <div className="pb-safe shrink-0 border-t border-line bg-surface px-4 pt-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ── Diversos ────────────────────────────────────────────────────────────────

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'primary' | 'warn' | 'danger' | 'ok';
}) {
  const tones = {
    neutral: 'bg-surface-sunken text-ink-soft',
    primary: 'bg-primary text-primary-ink',
    warn: 'bg-warn/15 text-warn',
    danger: 'bg-danger/15 text-danger',
    ok: 'bg-ok/15 text-ok',
  };

  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-semibold',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

export function Spinner() {
  return (
    <div className="flex justify-center py-10">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-line border-t-primary" />
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <p className="text-[15px] font-semibold text-ink">{title}</p>
      {description && <p className="max-w-xs text-[14px] text-ink-soft">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-[14px] text-danger">
      {children}
    </div>
  );
}

/**
 * Lista de escolhas em cartões grandes — serviço, profissional, pagamento.
 *
 * `selected` como lista liga a escolha múltipla, e com ela aparece o quadrado
 * de marcação. O sinal não é enfeite: quem escolheu "Cabelo" numa lista que
 * aprendeu a ler como "escolha uma" não tem motivo nenhum para tocar em
 * "Barba" também — e o recurso inteiro passaria despercebido.
 */
export function OptionList<T>({
  items,
  selected,
  onSelect,
  getKey,
  renderTitle,
  renderMeta,
}: {
  items: readonly T[];
  /** Uma chave, ou a lista das escolhidas quando dá para escolher mais de uma. */
  selected?: string | null | readonly string[];
  onSelect: (item: T) => void;
  getKey: (item: T) => string;
  renderTitle: (item: T) => ReactNode;
  renderMeta?: (item: T) => ReactNode;
}) {
  const multiple = Array.isArray(selected);

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => {
        const key = getKey(item);
        const active = multiple ? (selected as readonly string[]).includes(key) : selected === key;

        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(item)}
            aria-pressed={multiple ? active : undefined}
            className={cx(
              'flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3.5 text-left',
              active
                ? 'border-primary bg-primary/10'
                : 'border-line bg-surface-raised active:bg-surface-sunken',
            )}
          >
            <span className="flex min-w-0 flex-1 items-center gap-3">
              {multiple && (
                <span
                  aria-hidden
                  className={cx(
                    'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border text-[13px] font-bold',
                    active
                      ? 'border-primary bg-primary text-primary-ink'
                      : 'border-line bg-surface',
                  )}
                >
                  {/* Vazio quando não escolhido, e não um ✓ transparente: o
                      invisível continua no texto que o leitor de tela lê e no
                      que o barbeiro copia. */}
                  {active ? '✓' : ''}
                </span>
              )}
              <span className="truncate text-[15px] font-medium text-ink">{renderTitle(item)}</span>
            </span>
            {renderMeta && (
              <span className="shrink-0 text-[14px] text-ink-soft">{renderMeta(item)}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
